import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LiveKitRoom } from "@livekit/components-react";
import Room from "./room/Room";
import LiveKitHome from "./LiveKitHome";
import MeetingLeftView from "./MeetingLeftView";
import "./LiveKitHome.css";
import { fetchToken } from "./tokenService";
import { setActiveSessionId } from "./correlation";
import { LIVEKIT_WS_URL } from "./endpoints";
import { LIVEKIT_ROOM_OPTIONS } from "./roomOptions";
import { LiveKitSessionProvider } from "./LiveKitSessionContext";
import TelemetryBootstrap from "./TelemetryBootstrap";
import ClientLogSessionSetup from "./ClientLogSessionSetup";
import LocalObserverBootstrap from "./LocalObserverBootstrap";
import { reportClientLog, captureException } from "./clientLogReporter";
import {
  getStoredDisplayName,
  getStoredRoomId,
  setStoredDisplayName,
  setStoredRoomId,
} from "./livekitStorage";

/**
 * LiveKit session flow: show join page (LiveKitHome), then on join fetch token
 * and render the meeting room with user's room id, name, and audio/video preferences.
 * Query params roomid and name pre-fill the form.
 */
export default function LiveKitSessionPage() {
  const [searchParams] = useSearchParams();
  const [joined, setJoined] = useState(false);
  const [token, setToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [mediaDeviceError, setMediaDeviceError] = useState(null);
  const [leftMeeting, setLeftMeeting] = useState(null);

  const [roomId, setRoomId] = useState(() => {
    const fromUrl = (
      searchParams.get("roomid") ||
      searchParams.get("roomId") ||
      searchParams.get("room") ||
      ""
    ).trim();
    return (fromUrl || getStoredRoomId()).trim().toUpperCase();
  });
  const [nickname, setNickname] = useState(() => {
    const fromUrl = (
      searchParams.get("name") ||
      searchParams.get("nickname") ||
      searchParams.get("userId") ||
      ""
    ).trim();
    return fromUrl || getStoredDisplayName();
  });
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);

  const handleJoin = async () => {
    const room = roomId?.trim();
    const name = nickname?.trim();
    if (!room || !name) return;

    // TI-316: forward the server-issued sessionId (URL is authority) on VSS calls.
    const sessionId = (searchParams.get("sessionId") || "").trim();
    if (sessionId) setActiveSessionId(sessionId);

    setIsJoining(true);
    setTokenError(null);

    try {
      const t = await fetchToken({
        room,
        identity: name,
        role: "host",
      });
      setStoredDisplayName(name);
      setStoredRoomId(room);
      setToken(t);
      setJoined(true);
    } catch (err) {
      console.error("Failed to get token", err);
      captureException(err, "fetch_livekit_token");
      setTokenError(
        err?.message || "Failed to join. Check token server and try again."
      );
    } finally {
      setIsJoining(false);
    }
  };

  if (leftMeeting) {
    return (
      <MeetingLeftView
        roomId={leftMeeting.roomId}
        displayName={leftMeeting.displayName}
        onBackToHome={() => setLeftMeeting(null)}
        onJoinPageClick={() => setLeftMeeting(null)}
      />
    );
  }

  if (!joined || !token) {
    return (
      <>
        {tokenError && (
          <div className="livekit-session-error" role="alert">
            {tokenError}
          </div>
        )}
        <LiveKitHome
          roomId={roomId}
          setRoomId={setRoomId}
          nickname={nickname}
          setNickname={setNickname}
          onJoin={handleJoin}
          audioEnabled={audioEnabled}
          setAudioEnabled={setAudioEnabled}
          videoEnabled={videoEnabled}
          setVideoEnabled={setVideoEnabled}
          isJoining={isJoining}
        />
      </>
    );
  }

  return (
    <>
      {mediaDeviceError && (
        <div className="livekit-session-error livekit-session-error--warn" role="status">
          <span>{mediaDeviceError}</span>
          <button
            type="button"
            className="livekit-session-error__dismiss"
            onClick={() => setMediaDeviceError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
      <LiveKitSessionProvider accessToken={token}>
        <LiveKitRoom
          serverUrl={LIVEKIT_WS_URL}
          token={token}
          connect
          audio={
            audioEnabled
              ? {
                  echoCancellation: true,
                  noiseSuppression: true,
                  autoGainControl: true,
                }
              : false
          }
          video={videoEnabled ? { facingMode: "user" } : false}
          options={LIVEKIT_ROOM_OPTIONS}
          onMediaDeviceFailure={() => {
            setMediaDeviceError(
              "Camera or microphone could not start. Use Safari on iPhone/iPad, allow permissions, and open this site over HTTPS. You can still try toggling mic/camera in the meeting."
            );
          }}
          onError={(e) => {
            console.error("LiveKit error", e);
            reportClientLog("livekit_error", e?.message || String(e), {
              stack: e?.stack,
              context: { name: e?.name },
            });
          }}
          onDisconnected={() => {
            setLeftMeeting({
              roomId: roomId?.trim() || "",
              displayName: nickname?.trim() || "",
            });
            setJoined(false);
            setToken(null);
            setMediaDeviceError(null);
          }}
          style={{ height: "100vh" }}
        >
          <TelemetryBootstrap />
          <ClientLogSessionSetup />
          <LocalObserverBootstrap />
          <Room />
        </LiveKitRoom>
      </LiveKitSessionProvider>
    </>
  );
}
